import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface TenantFeaturePermission {
  id: string;
  tenant_id: string;
  feature_key: string;
  is_enabled: boolean;
  enabled_by_user_id?: string;
  enabled_at?: string;
  created_at: string;
  updated_at: string;
}

export function useTenantFeaturePermissions() {
  const { user, userRole } = useAuth();
  const [permissions, setPermissions] = useState<TenantFeaturePermission[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = async (tenantId?: string) => {
    if (!user || userRole !== 'superadmin') return;

    setLoading(true);
    try {
      let query = supabase
        .from('tenant_feature_permissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (tenantId) {
        query = query.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPermissions(data || []);
    } catch (error) {
      console.error('Error fetching tenant feature permissions:', error);
      toast.error('Fehler beim Laden der Feature-Berechtigungen');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeaturePermission = async (
    tenantId: string, 
    featureKey: string, 
    enabled: boolean
  ) => {
    if (!user || userRole !== 'superadmin') {
      toast.error('Keine Berechtigung');
      return false;
    }

    try {
      const { error } = await supabase
        .from('tenant_feature_permissions')
        .upsert({
          tenant_id: tenantId,
          feature_key: featureKey,
          is_enabled: enabled,
          enabled_by_user_id: enabled ? user.id : null,
          enabled_at: enabled ? new Date().toISOString() : null,
        }, {
          onConflict: 'tenant_id,feature_key'
        });

      if (error) throw error;

      toast.success(
        enabled 
          ? `Feature "${featureKey}" für Tenant aktiviert`
          : `Feature "${featureKey}" für Tenant deaktiviert`
      );

      // Refresh permissions
      await fetchPermissions();
      return true;
    } catch (error) {
      console.error('Error toggling feature permission:', error);
      toast.error('Fehler beim Ändern der Feature-Berechtigung');
      return false;
    }
  };

  const hasFeaturePermission = (tenantId: string, featureKey: string): boolean => {
    const permission = permissions.find(
      p => p.tenant_id === tenantId && p.feature_key === featureKey
    );
    return permission?.is_enabled || false;
  };

  useEffect(() => {
    if (userRole === 'superadmin') {
      fetchPermissions();
    }
  }, [user, userRole]);

  return {
    permissions,
    loading,
    fetchPermissions,
    toggleFeaturePermission,
    hasFeaturePermission,
  };
}